/**
 * Data Cache Utility
 * Handles local storage caching and batched backend saves
 * Provides instant feedback with background sync
 * 
 * E2E/Production Behavior:
 * - Cache entries expire after 2 hours (reduced from 24 hours for E2E)
 * - Only keeps 5 most recent session caches to prevent quota issues
 * - Automatically cleans up old entries on quota errors
 * - Session-scoped to prevent cross-user data conflicts
 */

const CACHE_KEY_PREFIX = 'skin_analysis_cache_';
const PENDING_SAVES_KEY = 'skin_analysis_pending_saves';

// Environment-specific cache settings
const isProduction = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENV === 'e2e';
const CACHE_MAX_AGE = isProduction ? 2 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000; // 2 hours for E2E/prod, 24 hours for dev
const MAX_CACHE_ENTRIES = isProduction ? 5 : 10; // Keep fewer entries in production

/**
 * Get cache key for a session
 */
const getCacheKey = (sessionId) => `${CACHE_KEY_PREFIX}${sessionId}`;

/**
 * Save data to localStorage immediately (instant, no delay)
 * Session-scoped to prevent conflicts between concurrent users
 * @param {string} sessionId - Session ID (required, must be unique per user)
 * @param {object} data - Data to cache
 */
export const cacheData = (sessionId, data) => {
  const sessionIdStr = sessionId != null ? String(sessionId).trim() : '';
  if (!sessionIdStr) {
    return false;
  }
  try {
    const cacheKey = getCacheKey(sessionIdStr);
    const existingData = getCachedData(sessionIdStr) || {};
    
    const mergedData = { 
      ...existingData, 
      ...data, 
      lastUpdated: Date.now(),
      sessionId: sessionIdStr
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(mergedData));
    return true;
  } catch (error) {
    // If localStorage is full, try to clean up old entries
    if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      cleanupOldCacheEntries();
      // Try again after cleanup
      try {
        const cacheKey = getCacheKey(sessionIdStr);
        const existingData = getCachedData(sessionIdStr) || {};
        const mergedData = { 
          ...existingData, 
          ...data, 
          lastUpdated: Date.now(),
          sessionId: sessionIdStr
        };
        localStorage.setItem(cacheKey, JSON.stringify(mergedData));
        return true;
      } catch (retryError) {
        // If still failing, clear all cache entries for this session
        try {
          clearCache(sessionIdStr);
          const cacheKey = getCacheKey(sessionIdStr);
          const newData = { 
            ...data, 
            lastUpdated: Date.now(),
            sessionId: sessionIdStr
          };
          localStorage.setItem(cacheKey, JSON.stringify(newData));
          return true;
        } catch (finalError) {
          return false;
        }
      }
    }
    return false;
  }
};

/**
 * Get cached data for a session
 * Validates session ID matches to prevent cross-session access
 * @param {string} sessionId - Session ID
 * @returns {object|null} Cached data or null
 */
export const getCachedData = (sessionId) => {
  try {
    const sid = sessionId != null ? String(sessionId).trim() : '';
    if (!sid) return null;
    
    const cacheKey = getCacheKey(sid);
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    
    if (data.sessionId && data.sessionId !== sid) return null;
    
    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Clear cached data for a session
 * @param {string} sessionId - Session ID
 */
export const clearCache = (sessionId) => {
  try {
    if (!sessionId) return;
    
    const cacheKey = getCacheKey(sessionId);
    localStorage.removeItem(cacheKey);
  } catch (error) {
  }
};

/**
 * Queue a save operation (for background sync)
 * Scoped by session ID to prevent conflicts between concurrent users
 * @param {string} sessionId - Session ID
 * @param {object} saveData - Data to save
 * @param {string} saveType - Type of save (userInfo, contactInfo, detection, goals)
 */
export const queueSave = (sessionId, saveData, saveType) => {
  try {
    if (!sessionId) {
      return;
    }
    
    // Get pending saves for this specific session
    const sessionPendingKey = `${PENDING_SAVES_KEY}_${sessionId}`;
    const pending = getPendingSavesForSession(sessionId);
    
    const saveEntry = {
      sessionId,
      saveType,
      data: saveData,
      timestamp: Date.now(),
      retries: 0
    };
    
    pending.push(saveEntry);
    localStorage.setItem(sessionPendingKey, JSON.stringify(pending));
  } catch (error) {
  }
};

/**
 * Get pending saves for a specific session
 * @param {string} sessionId - Session ID
 * @returns {Array} Array of pending save operations for this session
 */
export const getPendingSavesForSession = (sessionId) => {
  try {
    if (!sessionId) return [];
    
    const sessionPendingKey = `${PENDING_SAVES_KEY}_${sessionId}`;
    const pending = localStorage.getItem(sessionPendingKey);
    return pending ? JSON.parse(pending) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Get all pending saves (for all sessions) - used for cleanup
 * @returns {Array} Array of all pending save operations
 */
export const getAllPendingSaves = () => {
  try {
    const allPending = [];
    // Iterate through all localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${PENDING_SAVES_KEY}_`)) {
        try {
          const pending = JSON.parse(localStorage.getItem(key));
          if (Array.isArray(pending)) {
            allPending.push(...pending);
          }
        } catch (e) {
          // Skip invalid entries
        }
      }
    }
    return allPending;
  } catch (error) {
    return [];
  }
};

/**
 * Clear pending saves for a specific session
 * @param {string} sessionId - Session ID
 */
export const clearPendingSavesForSession = (sessionId) => {
  try {
    if (!sessionId) return;
    
    const sessionPendingKey = `${PENDING_SAVES_KEY}_${sessionId}`;
    localStorage.removeItem(sessionPendingKey);
  } catch (error) {
  }
};

/**
 * Clear all pending saves (for cleanup)
 */
export const clearAllPendingSaves = () => {
  try {
    // Remove all pending save keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${PENDING_SAVES_KEY}_`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
  }
};

/**
 * Remove a specific pending save for a session
 * @param {string} sessionId - Session ID
 * @param {number} index - Index of save to remove
 */
export const removePendingSave = (sessionId, index) => {
  try {
    if (!sessionId) return;
    
    const pending = getPendingSavesForSession(sessionId);
    pending.splice(index, 1);
    const sessionPendingKey = `${PENDING_SAVES_KEY}_${sessionId}`;
    localStorage.setItem(sessionPendingKey, JSON.stringify(pending));
  } catch (error) {
  }
};

/**
 * Clean up old cache entries (older than 24 hours)
 * Prevents localStorage from filling up with old session data
 */
export const cleanupOldCacheEntries = () => {
  try {
    const now = Date.now();
    const maxAge = CACHE_MAX_AGE;
    const keysToRemove = [];
    const allCacheKeys = [];
    
    // Collect all cache entries first
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        allCacheKeys.push(key);
      }
    }
    
    // Sort by lastUpdated if available, remove oldest first
    const cacheEntries = allCacheKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
        return { key, lastUpdated: data.lastUpdated || 0 };
        } catch (e) {
        return { key, lastUpdated: 0 };
      }
    }).sort((a, b) => a.lastUpdated - b.lastUpdated);
    
    // Remove old entries (older than maxAge)
    cacheEntries.forEach(entry => {
      if (entry.lastUpdated > 0 && (now - entry.lastUpdated) > maxAge) {
        keysToRemove.push(entry.key);
      }
    });
    
    // If still need space, remove oldest entries until we have room
    if (keysToRemove.length === 0 && cacheEntries.length > MAX_CACHE_ENTRIES) {
      // Keep only the most recent entries
      const entriesToKeep = cacheEntries.slice(-MAX_CACHE_ENTRIES);
      const entriesToKeepKeys = entriesToKeep.map(e => e.key);
      cacheEntries.forEach(entry => {
        if (!entriesToKeepKeys.includes(entry.key)) {
          keysToRemove.push(entry.key);
        }
      });
    }
    
    // Check pending saves
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${PENDING_SAVES_KEY}_`)) {
        try {
          const pending = JSON.parse(localStorage.getItem(key));
          if (Array.isArray(pending) && pending.length > 0) {
            const oldestTimestamp = Math.min(...pending.map(p => p.timestamp || 0));
            if (oldestTimestamp > 0 && (now - oldestTimestamp) > maxAge) {
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // Skip invalid entries
        }
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore errors when removing
      }
    });
  } catch (error) {
  }
};

